import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Fade,
  Grow
} from '@mui/material';
import { Send, SmartToy } from '@mui/icons-material';
import { TenantConfig } from '../tenant-config';

interface QAPair {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
}

interface ChatInterfaceProps {
  tenantConfig: TenantConfig;
  transactions: any[];
}

// Simple markdown renderer component
const MarkdownText: React.FC<{ children: string }> = ({ children }) => {
  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, index) => {
      // Handle bullet points
      if (line.trim().startsWith('•')) {
        const content = line.replace(/^•\s*/, '');
        const processedContent = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');

        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography component="span" sx={{ mr: 1, color: 'primary.main', fontWeight: 'bold' }}>
              •
            </Typography>
            <Typography
              component="span"
              variant="body2"
              dangerouslySetInnerHTML={{ __html: processedContent }}
              sx={{ '& strong': { fontWeight: 600, color: 'primary.main' } }}
            />
          </Box>
        );
      }

      // Handle arrows
      if (line.trim().startsWith('→')) {
        const content = line.replace(/^→\s*/, '');
        const processedContent = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');

        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography component="span" sx={{ mr: 1, color: 'success.dark', fontWeight: 'bold' }}>
              →
            </Typography>
            <Typography
              component="span"
              variant="body2"
              dangerouslySetInnerHTML={{ __html: processedContent }}
              sx={{
                color: 'success.dark',
                fontWeight: 500,
                '& strong': { fontWeight: 600 }
              }}
            />
          </Box>
        );
      }

      // Handle regular text with bold
      if (line.trim()) {
        const processedContent = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');

        return (
          <Typography
            key={index}
            variant="body2"
            sx={{ mb: 0.5, '& strong': { fontWeight: 600, color: 'primary.main' } }}
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        );
      }

      return <Box key={index} sx={{ height: 4 }} />; // Empty line spacing
    });
  };

  return <Box>{renderMarkdown(children)}</Box>;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ tenantConfig, transactions }) => {
  const [inputText, setInputText] = useState('');
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sample questions
  const sampleQuestions = [
    "Tình hình tài chính tháng này thế nào?",
    "Tôi nên cắt giảm chi tiêu ở đâu?",
    "Xu hướng thu chi có ổn không?",
    "Danh mục nào tôi chi nhiều nhất?",
    "Tôi có tiết kiệm được không?",
    "Tháng tới nên chú ý gì?"
  ];

  const convertTransactionsToText = () => {
    if (!transactions.length) return 'Chưa có dữ liệu giao dịch.';

    return transactions.map(t => {
      const shortDate = t.date.slice(5); // Remove year, keep MM-DD
      const type = t.type === 'income' ? '+' : '-';
      const amount = t.amount.toLocaleString();
      const desc = t.title ? ` (${t.title.slice(0, 20)})` : '';
      return `${shortDate} ${type}${amount} ${t.category}${desc}`;
    }).join('\n');
  };

  const sendMessage = async (questionText?: string) => {
    const question = questionText || inputText.trim();
    if (!question || isLoading) return;

    setInputText('');
    setIsLoading(true);
    setError(null);

    try {
      const transactionData = convertTransactionsToText();

      // Build conversation history context
      const conversationHistory = qaPairs.length > 0
        ? `\n\nLịch sử hội thoại trước đó:\n${qaPairs.map((pair, index) =>
          `${index + 1}. Hỏi: "${pair.question}"\n   Trả lời: "${pair.answer}"`
        ).join('\n')}`
        : '';

      const prompt = `Bạn là trợ lý tài chính chuyên nghiệp. Trả lời ngắn gọn, có cấu trúc rõ ràng.

${conversationHistory}

Câu hỏi hiện tại: ${question}

Yêu cầu định dạng trả lời:
- Sử dụng ký hiệu • cho danh sách
- Dùng ** để làm nổi bật số liệu quan trọng
- Chia thành các phần ngắn, dễ đọc
- Ưu tiên số liệu cụ thể và tỷ lệ phần trăm
- Tham khảo câu trả lời trước nếu có liên quan
- Tối đa 4-5 dòng, mỗi dòng 1 ý chính
- Kết thúc bằng 1 lời khuyên cụ thể

Ví dụ format:
• Thu nhập: **50,000,000đ** (+20% so với tháng trước)
• Chi tiêu: **35,000,000đ** (70% thu nhập)
• Tiết kiệm: **15,000,000đ** 
→ Nên tăng tỷ lệ tiết kiệm lên 25% thu nhập`;

      const response = await fetch(`https://aws-ai-worker.doannq01.workers.dev/ai/generate`, {
        // const response = await fetch(`http://localhost:8000/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': tenantConfig.customApiKey
        },
        body: JSON.stringify({
          data: transactionData,
          prompt: prompt
        })
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;

        if (response.status === 401) {
          errorMessage = 'API key không hợp lệ';
        } else if (response.status === 403) {
          errorMessage = 'Không có quyền truy cập API';
        } else if (response.status === 429) {
          errorMessage = 'Vượt quá giới hạn API';
        } else if (response.status >= 500) {
          errorMessage = 'Lỗi server API';
        }

        throw new Error(errorMessage);
      }

      const { data } = await response.json();
      console.log(data);
      const aiText = data.content || data.response || data.answer || data.result || 'Không nhận được phản hồi từ AI';

      // Clean up response - preserve markdown formatting
      const cleanedText = aiText
        .replace(/```[a-zA-Z]*\n?/g, '')
        .replace(/```/g, '')
        .trim();

      // Add new Q&A pair to the list
      const newQAPair: QAPair = {
        id: Date.now().toString(),
        question: question,
        answer: cleanedText,
        timestamp: new Date()
      };

      setQaPairs(prev => [...prev, newQAPair]);

    } catch (err) {
      console.error('API error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Lỗi khi gửi câu hỏi';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSampleQuestionClick = (question: string) => {
    sendMessage(question);
  };

  return (
    <Card
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          color: 'white',
          mx: -1
        }}>
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Trợ lý AI Tài chính
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Hỏi tôi về tình hình tài chính của bạn
            </Typography>
          </Box>
        </Box>

        {error && (
          <Fade in={!!error}>
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.2rem' }
              }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Sample Questions */}
        {qaPairs.length === 0 && !isLoading && (
          <Fade in={qaPairs.length === 0 && !isLoading} timeout={500}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                💡 Câu hỏi gợi ý
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 1.5
              }}>
                {sampleQuestions.map((question, index) => (
                  <Grow in={true} timeout={300 + index * 100} key={index}>
                    <Chip
                      label={question}
                      onClick={() => handleSampleQuestionClick(question)}
                      disabled={isLoading}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        height: 'auto',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                          py: 0.5
                        },
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        borderColor: 'primary.main',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                        }
                      }}
                    />
                  </Grow>
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {/* Conversation Display */}
        <Box sx={{
          minHeight: 200,
          mb: 3,
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          p: 3,
          border: '1px solid rgba(255,255,255,0.3)',
          maxHeight: 500,
          overflowY: 'auto'
        }}>
          {qaPairs.length === 0 && !isLoading ? (
            <Box sx={{
              textAlign: 'center',
              py: 6,
              color: 'text.secondary'
            }}>
              <SmartToy sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                Sẵn sàng hỗ trợ bạn!
              </Typography>
              <Typography variant="body2">
                Chọn câu hỏi gợi ý hoặc nhập câu hỏi của riêng bạn
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Display all Q&A pairs */}
              {qaPairs.map((pair, index) => (
                <Box key={pair.id} sx={{ mb: index < qaPairs.length - 1 ? 4 : 2 }}>
                  {/* Question */}
                  <Fade in={true} timeout={300}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: 2
                    }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          maxWidth: '85%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="body1" sx={{
                          fontWeight: 500,
                          lineHeight: 1.5
                        }}>
                          {pair.question}
                        </Typography>
                      </Paper>
                    </Box>
                  </Fade>

                  {/* Answer */}
                  <Fade in={true} timeout={500}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'flex-start'
                    }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          maxWidth: '85%',
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          borderRadius: 2
                        }}
                      >
                        <MarkdownText>{pair.answer}</MarkdownText>
                      </Paper>
                    </Box>
                  </Fade>
                </Box>
              ))}

              {/* Loading state */}
              {isLoading && (
                <Fade in={isLoading}>
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    p: 3,
                    textAlign: 'center',
                    justifyContent: 'center'
                  }}>
                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AI đang phân tích dữ liệu của bạn...
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Box>
          )}
        </Box>

        {/* Input */}
        <Box sx={{
          display: 'flex',
          gap: 1.5,
          background: 'rgba(255,255,255,0.9)',
          p: 1,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.3)',
          backdropFilter: 'blur(10px)'
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Nhập câu hỏi về tài chính..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'transparent',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.95rem'
              }
            }}
          />
          <IconButton
            onClick={() => sendMessage()}
            disabled={!inputText.trim() || isLoading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              width: 44,
              height: 44,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: 'scale(1.05)'
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChatInterface;
